import { useEffect } from 'react';

/**
 * Scroll-reveal for the scoped Grow & Raise landings (`.spx`, `.grx`, `.prtx`).
 *
 * Pass the scope's `.reveal` selector; every match is tagged `data-reveal` and
 * gains `.in` as it scrolls into view. The `data-reveal` attribute is added here
 * rather than authored into the markup so the CSS only hides elements this
 * component is actually managing — if the script never runs, nothing is left
 * invisible. One observer per page, one-shot per element.
 */
export default function Reveal({ selector }: { selector: string }) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const els = Array.from(document.querySelectorAll<HTMLElement>(selector));
    if (!els.length) return;

    const io = new IntersectionObserver(entries => {
      for (const e of entries) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }
    }, { rootMargin: '0px 0px -6% 0px', threshold: .1 });

    for (const el of els) {
      el.setAttribute('data-reveal', '');
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) el.classList.add('in');
      else io.observe(el);
    }
    return () => io.disconnect();
  }, [selector]);

  return null;
}
